import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Reveal } from "./Reveal";
import { Star } from "./Logo";
import { useLang } from "@/i18n/LanguageContext";
import { Camera, ScanSearch, Shirt, BookOpen, Store, Sparkles, ShoppingBag, ArrowRight } from "lucide-react";

const STEP_ICONS = { b2b: [Camera, ScanSearch, Shirt], b2c: [Store, Sparkles, ShoppingBag] };

const BeforeAfter = ({ w }) => {
  const [pos, setPos] = useState(50);
  return (
    <div className="relative select-none overflow-hidden rounded-[1.8rem] border-2 shadow-2xl" style={{ borderColor: "var(--gold)", aspectRatio: "848/1264" }} data-testid="window-looks-slider">
      <img src="/img/window-look-mannequin.jpg" alt={w.beforeLabel} className="absolute inset-0 h-full w-full object-cover" draggable={false} />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 0 0 ${pos}%)` }}>
        <img src="/img/window-look-customer.jpg" alt={w.afterLabel} className="h-full w-full object-cover" draggable={false} />
      </div>
      <div className="pointer-events-none absolute inset-y-0 w-0.5 bg-white/90 shadow-[0_0_12px_rgba(0,0,0,0.4)]" style={{ left: `${pos}%` }}>
        <span className="absolute left-1/2 top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[10px] font-bold shadow-lg" style={{ color: "var(--camel)" }}>⇄</span>
      </div>
      <span className="absolute left-3 top-3 rounded-full px-3 py-1 text-[10px] font-bold uppercase text-white backdrop-blur-sm" style={{ background: "rgba(44,39,37,0.55)", letterSpacing: "0.08em" }}>{w.beforeLabel}</span>
      <span className="absolute right-3 top-3 rounded-full px-3 py-1 text-[10px] font-bold uppercase text-white" style={{ background: "var(--gold)", letterSpacing: "0.08em" }}>{w.afterLabel}</span>
      <input
        type="range"
        min="0"
        max="100"
        value={pos}
        onChange={(e) => setPos(Number(e.target.value))}
        aria-label="Before / After"
        data-testid="window-looks-range"
        className="absolute inset-0 h-full w-full cursor-ew-resize opacity-0"
      />
    </div>
  );
};

export default function WindowLooks({ variant = "b2b" }) {
  const { t, lang } = useLang();
  const w = variant === "b2c" ? t.windowLooksB2c : t.windowLooks;
  const icons = STEP_ICONS[variant];
  const videoSrc = lang === "de" ? "/videos/schaufenster-b2b-de.mp4" : "/videos/schaufenster-b2b-en.mp4";
  const ldId = `ld-video-windowlooks-${variant}`;

  useEffect(() => {
    if (!w) return;
    document.getElementById(ldId)?.remove();
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = ldId;
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "VideoObject",
      name: w.title,
      description: w.sub,
      thumbnailUrl: "https://mira-mirror.ai/videos/schaufenster-b2b-poster.webp",
      contentUrl: `https://mira-mirror.ai${videoSrc}`,
      uploadDate: "2026-06-17T09:00:00+02:00",
      inLanguage: lang === "de" ? "de" : "en",
      publisher: { "@type": "Organization", name: "Mira Mirror" },
    });
    document.head.appendChild(ld);
    return () => document.getElementById(ldId)?.remove();
  }, [w, lang, videoSrc, ldId]);

  if (!w) return null;

  return (
    <section id="schaufenster" className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28" data-testid="window-looks-section">
      <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[minmax(0,420px)_1fr] lg:gap-16">
        <Reveal className="mx-auto w-full max-w-[380px]">
          <BeforeAfter w={w} />
          <p className="mt-3 text-center text-xs" style={{ color: "var(--muted-warm)" }}>{w.sliderHint}</p>
        </Reveal>

        <div>
          <Reveal>
            <p className="flex items-center gap-2.5 text-[11px] font-black uppercase" style={{ letterSpacing: "0.24em", color: "var(--camel)" }}>
              <span className="rounded-full px-2.5 py-1 text-[10px] font-black text-white" style={{ background: "var(--gold)", letterSpacing: "0.1em" }} data-testid="window-looks-new-badge">{w.badge}</span>
              <Star size={11} /> {w.kicker}
            </p>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight" data-testid="window-looks-title">{w.title}</h2>
            <p className="mt-4 max-w-xl text-base md:text-lg" style={{ color: "var(--muted-warm)" }}>{w.sub}</p>
          </Reveal>

          <div className="mt-8 space-y-4">
            {w.steps.map((s, i) => {
              const Icon = icons[i];
              return (
                <Reveal key={i} delay={i * 0.07} className="flex items-start gap-4 rounded-2xl border p-5" style={{ background: "var(--surface)", borderColor: "var(--line)" }} data-testid={`window-looks-step-${i + 1}`}>
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ background: "var(--rose-soft)" }}>
                    <Icon size={18} strokeWidth={1.7} style={{ color: "var(--camel)" }} />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold">{i + 1}. {s.t}</span>
                    <span className="mt-1 block text-[13px] leading-relaxed" style={{ color: "var(--muted-warm)" }}>{s.d}</span>
                  </span>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.2} className="mt-7 flex flex-wrap gap-2.5" data-testid="window-looks-facts">
            {w.facts.map((f, i) => (
              <span key={i} className="rounded-full border px-4 py-1.5 text-xs font-medium" style={{ borderColor: "var(--line)", color: "var(--muted-warm)", background: "var(--surface-secondary)" }}>
                ✦ {f}
              </span>
            ))}
          </Reveal>

          <Reveal delay={0.25}>
            <p className="mt-7 text-sm font-semibold" style={{ color: "var(--camel)" }} data-testid="window-looks-claim">«{w.claim}»</p>
            <Link
              to="/blog/schaufenster-looks-boutique"
              data-testid="window-looks-article-link"
              className="gold-underline mt-4 inline-flex items-center gap-1.5 text-sm font-medium"
              style={{ color: "var(--camel)" }}
            >
              <BookOpen size={15} strokeWidth={1.8} /> {w.more} →
            </Link>
            {variant === "b2c" && (
              <p className="mt-5 text-xs" style={{ color: "var(--muted-warm)" }} data-testid="window-looks-boutique-note">
                {w.boutiqueNote}{" "}
                <Link to="/business" data-testid="window-looks-boutique-link" className="gold-underline inline-flex items-center gap-1 font-medium" style={{ color: "var(--camel)" }}>
                  {w.boutiqueCta} <ArrowRight size={12} />
                </Link>
              </p>
            )}
          </Reveal>
        </div>
      </div>

      <Reveal delay={0.1} className="mx-auto mt-14 max-w-4xl">
        <div className="overflow-hidden rounded-[1.8rem] border-2 shadow-xl" style={{ borderColor: "var(--line)", background: "#000" }} data-testid="window-looks-video">
          <video
            src={videoSrc}
            poster="/videos/schaufenster-b2b-poster.webp"
            controls
            playsInline
            preload="metadata"
            className="aspect-video w-full object-cover"
            onError={(e) => { if (e.currentTarget.src.includes("-en")) e.currentTarget.src = "/videos/schaufenster-b2b-de.mp4"; }}
          />
        </div>
        <p className="mt-3 text-center text-xs" style={{ color: "var(--muted-warm)" }} data-testid="window-looks-video-label">{w.videoLabel}</p>
      </Reveal>
    </section>
  );
}
